/**
 * "Your lab report, explained": one entry per parameter on our two lab
 * reports. Source notes live in docs/content/LAB_PARAMETERS_RESEARCH.md.
 *
 * Rules for this file (FSSAI Advertising and Claims Regulations 2018, the
 * Ayurveda Aahara Regulations 2022 do not cover plain coconut oil):
 *  - `science` explains what the result means for the buyer. Purity tests
 *    are identity and adulteration checks; they are not health benefits.
 *  - `ayurveda` only where a genuine link exists, always attributed
 *    ("classical texts describe") and never as a product promise. Where
 *    there is no link we say so rather than force one.
 *  - Numbers come from brand.js so they can never drift.
 */
import { LAB_REPORT, PURITY_REPORT } from "@/content/brand";

const p = (param) => PURITY_REPORT.rows.find((r) => r.param.startsWith(param));
const n = (param) => LAB_REPORT.rows.find((r) => r.param === param);
const nv = (param) => `${n(param).value} ${n(param).unit}`;

export const AYURVEDA_HERITAGE = {
  intro:
    "In Ayurveda, coconut is Narikela and coconut oil is Narikela taila. Classical texts, as summarised in modern Ayurvedic reviews, describe the oil as madhura (sweet), guru (heavy), snigdha (unctuous) and sheeta virya (cooling in its effect), and as calming Vata and Pitta. It is also described as keshya, a word for things that are good for the hair. In South India it has been the everyday oil for abhyanga (oil massage) and shiro abhyanga (head oiling) for generations.",
  note:
    "We share this as heritage, not as a promise. Karijeeva is a food, not an Ayurvedic medicine, and we do not claim it treats or balances anything in your body. If you follow Ayurveda for a health reason, speak to a qualified vaidya.",
  terms: [
    ["Narikela taila", "Coconut oil"],
    ["Madhura", "Sweet in taste"],
    ["Guru", "Heavy, slow to digest"],
    ["Snigdha", "Unctuous, oily"],
    ["Sheeta virya", "Cooling in its effect"],
    ["Keshya", "Traditionally described as good for hair"],
    ["Abhyanga", "Oil massage of the body"],
    ["Shiro abhyanga", "Oil massage of the head and scalp"],
    ["Gandusha", "Holding oil in the mouth, the root of oil pulling"],
  ],
  sources: [
    { label: "A review on Narikela Thaila and Narikela regarding its use and properties, Journal of Ayurveda and Integrated Medical Sciences", url: "https://jaims.in/jaims/article/view/1764" },
    { label: "Narikela (Cocos nucifera L.): unique medicinal recipes from classical texts, Journal of Drug Research in Ayurvedic Sciences (CCRAS), 2025", url: "https://journals.lww.com/jdra/fulltext/2025/03000/narikela__cocos_nucifera_l____unique_medicinal.3.aspx" },
    { label: "A review of Taila Varga (edible oils) according to the Bruhat Trayi, International Journal of Ayurveda and Pharma Research", url: "https://ijapr.in/index.php/ijapr/article/view/2938" },
  ],
};

export const PURITY_EXPLAINED = [
  {
    key: "br",
    name: "Butyro refractometer reading at 40°C",
    value: p("Butyro").value,
    limit: p("Butyro").limit,
    what: "How much the oil bends light, read at 40°C when coconut oil is fully liquid. Every oil has its own range, so this is a quick identity check.",
    result: "34.8 sits inside the FSSAI range of 34.0 to 35.5.",
    science: "Coconut oil reads low because it is made mostly of short and medium chain saturated fats. Palm oil, seed oils and liquid paraffin bend light more, so mixing them in pushes the reading up. On its own it can't catch every kind of mixing, which is why it is read together with the tests below.",
    ayurveda: null,
  },
  {
    key: "sap",
    name: "Saponification value",
    value: p("Saponification").value,
    limit: p("Saponification").limit,
    what: "How much alkali it takes to turn one gram of oil into soap. Oils made of shorter fatty acids have more molecules per gram, so they need more.",
    result: "254, above the FSSAI minimum of 250.",
    science: "Coconut oil scores around 250 to 260 because it is rich in lauric, myristic, caprylic and capric acids. Groundnut, sunflower and palm oil score about 190 to 200, so blending them in pulls the number down.",
    ayurveda: null,
  },
  {
    key: "iv",
    name: "Iodine value",
    value: p("Iodine").value,
    limit: p("Iodine").limit,
    what: "How much iodine the oil takes up. Iodine attaches to double bonds, so this measures how unsaturated the oil is.",
    result: "8.1, inside the FSSAI range of 7.5 to 10, in its lower part.",
    science: "A low iodine value is why coconut oil resists going rancid and why it sets solid when the weather turns cool: there are very few double bonds for oxygen to attack. It is also a strong blending check, because palm oil scores around 50 and most seed oils above 100. The flip side is honest too: a very saturated oil is one to use in sensible amounts.",
    ayurveda: "Our own reading, not a classical statement: an oil this saturated, that turns solid in the cold, fits the way Ayurvedic texts describe Narikela taila, as guru (heavy) and snigdha (unctuous).",
  },
  {
    key: "unsap",
    name: "Unsaponifiable matter",
    value: p("Unsaponifiable").value,
    limit: p("Unsaponifiable").limit,
    what: "The small part of an oil that does not turn into soap: natural sterols, vitamin E and pigments, plus anything foreign such as mineral oil.",
    result: "0.5%, within the FSSAI maximum of 1.0%.",
    science: "Mineral oil and liquid paraffin never turn into soap, so they show up here. A figure well inside the limit is a good sign that nothing of that kind has been added.",
    ayurveda: null,
  },
  {
    key: "av",
    name: "Acid value",
    value: p("Acid").value,
    limit: p("Acid").limit,
    what: "The amount of free fatty acids, the fatty acids that have come loose from the oil's structure, usually because of moisture in the copra. An acid value of 4.7 equals about 1.7% free fatty acids, counted as lauric acid.",
    result: "4.7, within the FSSAI maximum of 6.0.",
    science: "This is the number we are working to bring down. It is typical for oil pressed from copra, but it is not low, and the international Codex standard for cold pressed oils sets 4.0. Free fatty acids rise when copra is not dried quickly and kept dry, so drying and storage are what decide this number, batch after batch.",
    ayurveda: null,
  },
  {
    key: "arg",
    name: "Argemone oil test",
    value: p("Argemone").value,
    limit: p("Argemone").limit,
    what: "A colour test for oil from argemone, a weed whose seeds look like mustard seeds.",
    result: "Negative, as FSSAI requires for every edible oil.",
    science: "Argemone oil contains sanguinarine, which causes epidemic dropsy. The largest outbreak, in Delhi in 1998, came from contaminated mustard oil and left more than 60 people dead. It is not a known coconut oil problem, but the law asks every edible oil to be tested, and we are glad it does.",
    ayurveda: null,
  },
];

export const NUTRITION_EXPLAINED = [
  {
    key: "energy",
    name: "Energy",
    value: nv("Energy"),
    what: "Calories, calculated from the fat, carbohydrate and protein figures.",
    science: "Every pure oil carries about 880 to 900 kcal per 100 g, so coconut oil is not lighter or heavier than others. A teaspoon (about 4.5 g) is roughly 40 kcal. ICMR NIN suggests most adults keep visible fat to about 20 to 30 g a day across all oils.",
    ayurveda: null,
  },
  {
    key: "fat",
    name: "Total fat",
    value: nv("Total fat"),
    what: "The share of the oil that is fat.",
    science: "Close to 100 g, as it should be for a pure oil. Nothing hidden in it.",
    ayurveda: null,
  },
  {
    key: "sfa",
    name: "Saturated fat",
    value: nv("Saturated fat"),
    what: "Fat with no double bonds. In coconut oil it is mostly lauric acid, with myristic, palmitic, caprylic and capric acids.",
    science: "This is what makes coconut oil stable in the kitchen and solid in the cold. It also raises LDL cholesterol: a 2020 analysis of 16 trials in the journal Circulation found it raised LDL more than unsaturated vegetable oils (and less than butter). Coconut oil is not the same as MCT oil. Use it as one of the oils in your kitchen, which is also what ICMR NIN advises.",
    ayurveda: "Our own reading: a highly saturated oil matches the texts' description of Narikela taila as guru and snigdha. We don't use Ayurveda to soften the cholesterol evidence.",
  },
  {
    key: "mufa",
    name: "Monounsaturated fat",
    value: nv("Monounsaturated fat"),
    what: "Fat with one double bond, mainly oleic acid.",
    science: "A small share, normal for coconut oil. Groundnut and olive oil are far richer in it, which is one reason a mix of oils makes sense.",
    ayurveda: null,
  },
  {
    key: "pufa",
    name: "Polyunsaturated fat",
    value: nv("Polyunsaturated fat"),
    what: "Fat with two or more double bonds, mainly linoleic acid.",
    science: "Very low, and this is coconut oil's real cooking strength. Oils high in polyunsaturated fat break down much faster when heated, so coconut oil holds up well to everyday tempering and sautéing. Still, never let any oil smoke, and don't reuse oil after deep frying.",
    ayurveda: null,
  },
  {
    key: "tfa",
    name: "Trans fat",
    value: nv("Trans fat"),
    what: "Fat with trans double bonds, mostly created by partial hydrogenation or high temperature refining.",
    science: "Below 0.1 g, against FSSAI's limit of 2% for oils and fats. Expected for an oil that is never refined or hydrogenated.",
    ayurveda: null,
  },
  {
    key: "chol",
    name: "Cholesterol",
    value: nv("Cholesterol"),
    what: "Cholesterol is made only by animals.",
    science: "Zero, as in every plant oil. We don't treat this as a selling point: for blood cholesterol, the saturated fat above matters far more.",
    ayurveda: null,
  },
  {
    key: "carb",
    name: "Total carbohydrates",
    value: nv("Total carbohydrates"),
    what: "Sugars, starch and fibre.",
    science: "Oil contains no sugar or starch. The lab lists this figure as calculated in house, most likely by difference (what's left after fat, protein, moisture and ash). Read it as zero.",
    ayurveda: null,
  },
  {
    key: "protein",
    name: "Protein",
    value: nv("Protein"),
    what: "Protein content.",
    science: "A trace, from fine particles of kernel. Nutritionally zero.",
    ayurveda: null,
  },
  {
    key: "fibre",
    name: "Fibre",
    value: nv("Fibre"),
    what: "Dietary fibre.",
    science: "None, as expected for an oil.",
    ayurveda: null,
  },
];

export const EXPLAINED_SOURCES = [
  { label: "FSSAI Food Products Standards, Chapter 2.2: Fats, oils and fat emulsions (coconut oil, 2.2.1(1))", url: "https://www.fssai.gov.in/upload/uploadfiles/files/Chapter%202_2_Fats_oils%20and%20fat%20emulsions.pdf" },
  { label: "FSSAI Manual of Methods of Analysis: Oils and Fats", url: "https://fssai.gov.in/upload/uploadfiles/files/Manual_Oil_Fat_25_05_2016(1).pdf" },
  { label: "Codex Standard for Named Vegetable Oils, CXS 210 1999", url: "https://www.fao.org/fao-who-codexalimentarius/sh-proxy/tr/?lnk=1&url=https%3A%2F%2Fworkspace.fao.org%2Fsites%2Fcodex%2FStandards%2FCXS+210-1999%2FCXS_210e.pdf" },
  { label: "Neelakantan, Seah and van Dam, The effect of coconut oil consumption on cardiovascular risk factors, Circulation 2020", url: "https://www.ahajournals.org/doi/10.1161/CIRCULATIONAHA.119.043052" },
  { label: "Sharma et al., epidemic dropsy in Delhi, Tropical Doctor 2002", url: "https://doi.org/10.1177/004947550203200204" },
  { label: "FSSAI: trans fat limit of 2% in oils and fats from January 2022", url: "https://fssai.gov.in/upload/media/FSSAI_News_TransFat_FNB_18_02_2021.pdf" },
];
